// @ts-check
import Vue from 'vue';
import { state } from '../state';
import { BETSLIP_MESSAGE_KEY, BETSLIP_MESSAGE_TYPE } from 'bto-models/betslip';
import { getItemHashKey, saveState } from './helpers';
import { getTotalMatchItems } from './items';
import { calculateBonuses, calculateSingleBonus } from './calculations';
import { addBetslipMessage, removeBetslipMessage } from './message.store';
import { configStore } from './config.store';
import { specialBetCombStore } from './special-bet-comb.store';
import { betslipStore } from '..';

function clearStakeMessages() {
	removeBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE);
	removeBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE_LIMIT);
}

/**
 * Check if we can add stake for given fold
 * @param {string} fold - new fold stake to check for
 * @returns true if validation fails
 */
function validateLines(fold) {
	// If fold is already set validation passed for this fold already no need to revalidate
	if (state.folds[fold]) {
		return;
	}
	let folds = Object.keys(state.folds);
	folds.push(fold);
	if (fold === '-1') {
		folds = Object.keys(state.foldLines);
	}
	const linesCount = folds.reduce((sum, k) => sum += (+state.foldLines[k]), 0);
	if (linesCount >= configStore.settings.MAX_LINES) {
		const msg = 'Mobile.maxcombnum';
		const translatedMsg = Vue.prototype.$translate.text(msg);
		const message = {
			type: BETSLIP_MESSAGE_TYPE.WARNING,
			message: `${translatedMsg} ${configStore.settings.MAX_LINES}`,
		};
		addBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE_LIMIT, message);
		return true;
	}
	return false;
}

/**
 * Validate Stake Amount 
 * @param {number} stake - stake amount to check against limits
 * @param {boolean} isLine - optional flag if stake is system or combi/single
 * @returns corrected stake 
 */
function validateStake(stake, isLine = false) {
	if (!stake) {
		return stake;
	}
	const minAmount = isLine ? configStore.settings.MIN_LINE_AMOUNT : configStore.settings.MIN_BET_AMOUNT;
	const maxAmount = isLine ? configStore.settings.MAX_LINE_AMOUNT : configStore.settings.MAX_BET_AMOUNT;

	if (stake < minAmount) {
		const msg = isLine ? 'Mobile.LineStakeLessThanMin' : 'Mobile.StakeLessThanMin'
		const translatedMsg = Vue.prototype.$translate.text(msg).replace('{0}', minAmount);
		const message = {
			type: BETSLIP_MESSAGE_TYPE.WARNING,
			message: translatedMsg,
		};
		addBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE_LIMIT, message);
		return minAmount;
	}

	if (stake > maxAmount) {
		const msg = isLine ? 'Mobile.LineStakeMoreThanMax' : 'Mobile.StakeMoreThanMax';
		const translatedMsg = Vue.prototype.$translate.text(msg).replace('{0}', maxAmount);

		const message = {
			type: BETSLIP_MESSAGE_TYPE.WARNING,
			message: translatedMsg,
		};
		addBetslipMessage(BETSLIP_MESSAGE_KEY.STAKE_LIMIT, message);
		return maxAmount;
	}

	return stake;
}

/**
 * Set single stake on betslip item
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 * @param {string} stake - single stake
 */
export const setSingleStake = (item, stake) => {
	const itemKey = getItemHashKey(item);
	// If saved single has same stake value as new stake return
	if (state.singles[itemKey] === stake) {
		return;
	}
	clearStakeMessages();
	let _stake = validateStake(+stake);
	if (!_stake) {
		Vue.delete(state.singles, itemKey);
	} else {
		Vue.set(state.singles, itemKey, `${_stake}`);
	}
	calculateSingleBonus(itemKey);
	state.calculationInProgress = true;
	calculateBonuses();
	saveState();
};

/**
 * Set quickbet stake
 * @param {string} stake - stake amount
 * @returns 
 */
export function setQuickBetStake(stake) {
	if (betslipStore.freebetId !== 0) {
		return;
	}
	if (state.quickBetStake === stake) {
		return;
	}
	clearStakeMessages();
	let _stake = validateStake(+stake);
	state.quickBetStake = _stake ? `${_stake}` : '';
	saveState();
}

/**
 * Validate APP_SETTINGS stakes for quickbet
 * @param {number[]} stakes - list of set stakes to validate
 */
export function validateQuickBetStakeSettings(stakes) {
	const validStakes = stakes.filter(s => s === validateStake(s)).map(s => `${s}`);
	clearStakeMessages();
	return validStakes;
}

/**
 * Set all folds stake to given stake
 * @param {string} stake - fold stake
 */
const setAllFoldStake = (stake) => {
	if (!(+stake)) {
		state.folds = {};
		return;
	}
	Object.keys(state.foldLines).forEach(fold => {
		Vue.set(state.folds, fold, stake);
	});
};

/**
 * Set specail bet folds stake to given stake
 * @param {string} id - specialbet combination id
 * @param {string} stake - special bet fold stake
 */
export const setSpecialBetFoldStake = (id, stake) => {
	// Reset sbc stakes if any were set
	specialBetCombStore.stakes = {};
	const noStake = !(+stake);
	if (!noStake) {
		specialBetCombStore.stakes[id] = stake;
	}

	const specialBetComb = specialBetCombStore.specialBetCombs[id];
	specialBetComb.inclCombinations.forEach(fl => {
		if (noStake) {
			Vue.delete(state.folds, fl);
			return;
		}
		Vue.set(state.folds, fl, stake);
	});
};

/**
 * Check for specail bet combinations set stakes, and if any special bet combination
 * containts the fold reset the stake
 * @param {number} fold - fold
 */
function resetSbcStake(fold) {
	Object.keys(specialBetCombStore.stakes).forEach(key => {
		if (specialBetCombStore.specialBetCombs[key].inclCombinations.includes(fold)) {
			Vue.delete(specialBetCombStore.stakes, key)
		}
	});
}

/**
 * Set stake for given fold
 * @param {string} fold - combination fold
 * @param {string} stake - fold stake
 */
export const setFoldStake = (fold, stake) => {
	// If saved fold has same stake value as new stake return
	if ((state.folds[fold] || '') === stake) {
		return;
	}
	const sbcFold = fold.replace('sbc', '');
	const isSbcFold = fold !== sbcFold;
	// If fold is sbc and saved sbc fold has same value as new stake return
	if (isSbcFold && (specialBetCombStore.stakes[sbcFold] || '') === stake) {
		return;
	}
	clearStakeMessages();
	const matchCount = `${getTotalMatchItems()}`;
	const isCombi = state.foldLines[fold] === 1 && fold === matchCount;
	let _stake = validateStake(+stake, !isCombi);

	if (!isCombi && validateLines(fold)) {
		_stake = '';
	}

	if (fold === '-1') {
		specialBetCombStore.stakes = {};
		setAllFoldStake(`${_stake}`);
	}

	// If fold is different from all combinations fold delete all combinations fold
	if (fold !== '-1' && state.folds['-1']) {
		Vue.delete(state.folds, '-1');
	}
	if (!_stake) {
		Vue.delete(state.folds, fold);
	} else if (!isSbcFold) {
		Vue.set(state.folds, fold, `${_stake}`);
	}

	if (isSbcFold) {
		setSpecialBetFoldStake(sbcFold, `${_stake}`);
	}

	if (!isSbcFold) {
		resetSbcStake(+fold);
	}

	state.calculationInProgress = true;
	calculateBonuses();
	saveState();
};

/** ===================================================
 * 	GETTERS SECTION
 * ====================================================
 */

/**
 * Get combo stake
 */
export const getComboStake = () => state.folds[getTotalMatchItems()];